import { supabase } from '@/utils/supabase';

export type EmploymentStatus = 'active' | 'pending' | 'terminated';

export interface EncompassEmploymentStatusResult {
  status: string;
  userId: string;
  employmentStatus: EmploymentStatus;
  encompassId?: string | null;
  remoteAccountUpdated: boolean;
  remoteAccessEnabled?: boolean | null;
  warning?: string | null;
}

async function getAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  if (!token) {
    throw new Error('Session expired. Please refresh the page.');
  }

  return token;
}

export async function setUserEmploymentStatusWithEncompass(
  userId: string,
  employmentStatus: EmploymentStatus
): Promise<EncompassEmploymentStatusResult> {
  const token = await getAccessToken();

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      action: 'set-employment-status',
      userId,
      employmentStatus
    })
  });

  // Read as text first so a proxy/runtime error returning HTML doesn't crash JSON.parse.
  const rawBody = await response.text();
  let parsed: unknown = null;
  try {
    parsed = rawBody ? JSON.parse(rawBody) : null;
  } catch {
    // Non-JSON body (proxy HTML error page, Cloudflare 502, etc.) — fall through with parsed=null.
  }

  if (!response.ok) {
    const errorMessage =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as { error?: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : null) ??
      (rawBody && rawBody.length <= 200 ? rawBody : null) ??
      `Failed to update employment status (HTTP ${response.status})`;
    throw new Error(errorMessage);
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Employment status update returned an unexpected response');
  }

  return parsed as EncompassEmploymentStatusResult;
}
